/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { Metadata } from './metadata';
import { VideoDestroyedError } from '../../errors/video-destroyed.error';
import { httpService } from 'amb/services/http.service';

/**
 * Abstract video model
 *
 * @typedef {import('./player').Player} Player
 */
export class AbstractVideo {
  static TYPE = null;
  static PARAMS = [];
  static EVENTS = [];
  static ID_PATTERN;
  static TYPE_PATTERN;

  /**
   * @type {Metadata} Video metadata adapter
   */
  metadata;

  /**
   * @type {Player} Video player adapter
   */
  player;

  /**
   * @type {object}
   */
  position;

  /**
   * This flag controlled by `amb-intersection` directive.
   *
   * @type {boolean}
   */
  visible = false;

  /**
   * @protected
   */
  initialized = false;

  /**
   * @protected
   */
  destroyed = false;

  /**
   * Constructor
   *
   * @param {HTMLElement} element
   * @param {object}      options
   */
  constructor(element, options) {
    const { PARAMS, EVENTS } = this.constructor;

    this.element = element;
    this.id = options.id || options.defaultVideoId;
    this.defaultVideoId = options.defaultVideoId;

    options = Object.assign({ clip: 0 }, options);
    this.options = omit(options, [...PARAMS, ...EVENTS]);
    this.options.params = pick(options, PARAMS);
    this.options.events = pick(options, EVENTS);
    this.originOptions = cloneDeep(this.options);

    if (options.responsiveOptions) {
      this.setOptions(options.responsiveOptions);
    }

    options.lazy ? setTimeout(() => this.loadMetadata()) : this.init();
  }

  /**
   * @abstract
   * @protected
   */
  get oembedUrl() {}

  /**
   * Create video player for corresponding video provider
   *
   * @abstract
   * @protected
   *
   * @returns {Player} The video player adapter
   */
  createPlayer(metadata) {}

  /**
   * Initial video
   *
   * @returns {Promise<Player>}
   */
  async init() {
    if (!this.isInitialized()) {
      this.initPromise = this.initPromise || this
        .loadMetadata()
        .then(metadata => this.isDestroyed() ? Promise.reject(new VideoDestroyedError()) : metadata)
        .then(metadata => this.createPlayer(metadata))
        .then(player => this.registerEvents(player))
        .then(player => {
          this.initialized = true;

          return player;
        })
        .catch(error => this.handleError(error))
        .finally(() => delete this.initPromise)
      ;

      return this.initPromise;
    }

    return Promise.resolve(this.player);
  }

  /**
   * Destroy the initialized video
   */
  destroy() {
    this.initialized = false;
    this.destroyed = true;
    delete this.metadata;

    if (this.player) {
      this.player.unload();
      this.player.destroy();

      delete this.player;
    }
  }

  /**
   * Checks if the video instance is initialized or not
   *
   * @returns {boolean} Returns `true` if video is initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Checks if the video element removed from DOM or not
   *
   * @returns {boolean} Returns `true` if video element has been removed
   */
  isDestroyed() {
    return this.destroyed;
  }

  /**
   * Update video options
   *
   * @param {object} options New options
   */
  setOptions(options) {
    for (const option in options) {
      const currentValue = this.options[option];
      const value = options[option];

      if (value !== currentValue) {
        this.options[option] = value;

        switch (option) {
          case 'autopause': this.player.setAutopause(!!value).catch(this.handleError); break;
          case 'clip': this.calculateSize(); break;
          case 'color': this.player.setColor(value).catch(this.handleError); break;
          case 'loop': this.player.setLoop(!!value).catch(this.handleError); break;
          case 'mute':
          case 'muted': this.player.setMuted(!!value).catch(this.handleError); break;
          case 'ratio': this.calculateSize(); break;
          case 'speed': this.player.setPlaybackRate(value).catch(this.handleError); break;
          default: console.warn(`Not supported set option: ${option}`);
        }
      }
    }
  }

  /**
   * Load new video by ID
   *
   * @param {string|number} id The video ID to load
   *
   * @returns {Promise<Player>}
   */
  async loadVideo(id) {
    if (id !== this.id && this.isInitialized()) {
      return this.player.isPlaying()
        .then((isPlaying) => {
          const oldVideo = this.id;
          this.id = id;
          delete this.metadata;

          // Update related thumbnails with new ID
          document
            .querySelectorAll(`[data-thumbnail="${oldVideo}"]`)
            .forEach(thumbnail => (thumbnail.dataset.thumbnail = id))
          ;

          return this.loadMetadata()
            .then(metadata => this.isDestroyed() ? Promise.reject(new VideoDestroyedError()) : metadata)
            .then(() => this.player.loadVideo(id))
            .then(() => (isPlaying || this.options.readonly) ? this.player.play() : null)
            .then(() => this.player)
          ;
        })
        .catch(error => this.handleError(error))
      ;
    }

    return Promise.resolve(this.player);
  }

  /**
   * Handle video playback errors
   *
   * @param {Error} error The occurred error
   */
  handleError(error) {
    switch (error.name) {
      // The video element has been removed from DOM while initializing. Ignore this error.
      case VideoDestroyedError.NAME: break;

      // Vimeo: If error is `PlayInterrupted`, just print the error message as a warning
      case 'PlayInterrupted': console.warn(error.message); break;

      // Otherwise, log the error to avoid functionality break
      default: console.error(error);
    }
  }

  /**
   * Converts the given params from `boolean` to `number` and vice versa
   *
   * Type: `bool`
   *  - 1 -> `true`
   *  - 0 -> `false`
   *
   * Type: `num`
   *  - `true` -> 1
   *  - `false` -> 0
   *
   * @protected
   *
   * @param {object} params
   * @param {'bool' | 'num'} type
   *
   * @returns {object} The converted params object
   */
  convertParams(params, type = 'num') {
    if (isPlainObject(params)) {
      for (const prop in params) {
        if (type === 'num' && typeof params[prop] === 'boolean') {
          params[prop] = params[prop] ? 1 : 0;
        } else if (type === 'bool' && typeof params[prop] === 'boolean') {
          params[prop] = params[prop] === 1 ? true : (params[prop] === 0 ? false : params[prop]);
        }
      }
    }

    return params;
  }

  /**
   * Load video metadata
   *
   * @protected
   *
   * @returns {Promise<Metadata>}
   */
  async loadMetadata() {
    if (!this.metadata && !this.isDestroyed() && this.oembedUrl) {
      return this.metadataPromise || httpService
        .get(this.oembedUrl)
        .then(response => response.json())
        .then(metadata => this.onMetadataLoaded(metadata))
        .catch(error => this.handleError(error))
        .finally(() => delete this.metadataPromise)
      ;
    }

    return Promise.resolve(this.metadata);
  }

  /**
   * @protected
   *
   * @param {object} metadata The video metadata from oembed response
   *
   * @returns {Metadata} The transformed metadata object
   */
  onMetadataLoaded(metadata) {
    if (!this.isDestroyed()) {
      const classList = this.element.classList;

      this.metadata = new Metadata(metadata, { type: this.constructor.TYPE, video_id: this.id });
      this.calculateSize();
      this.element.classList.add('size-calculated');

      if (this.options.size === 'cover') {
        classList.add('aspect-ratio');
      }

      if (typeof this.options.onMetaLoaded === 'function') {
        this.options.onMetaLoaded(this, this.metadata);
      }

      return this.metadata;
    }
  }

  /**
   * Register video events configured in options
   *
   * @protected
   *
   * @param {Player} player Video player adapter
   *
   * @returns {Player} Video player adapter
   */
  registerEvents(player) {
    if (!this.isDestroyed()) {
      for (const name in this.options.events) {
        if (typeof this.options.events[name] === 'function') {
          player.on(name, (...args) => this.options.events[name](this, ...args));
        }
      }
    }

    return player;
  }

  /**
   * @protected
   *
   * @param {HTMLElement} element
   */
  applyClip(element) {
    if (this.position && this.options.size === 'cover') {
      element.style.height = (100 / this.position.vertical) + '%';
      element.style.top = -(100 / this.position.vertical * this.position.top) + '%';

      element.style.width = (100 / this.position.horizontal) + '%';
      element.style.left = -(100 / this.position.horizontal * this.position.left) + '%';
    }
  }

  /**
   * @protected
   *
   * @param {object} position
   */
  setPosition(position) {
    if (isEqual(this.position, position)) {
      return;
    }

    const classList = this.element.classList;

    this.position = position;
    this.element.style.setProperty('padding-bottom', `${position.percentageHeight}%`);

    classList.toggle('square', this.position.percentageHeight === 100);
    classList.toggle('landscape', this.position.percentageHeight < 100);
    classList.toggle('portrait', this.position.percentageHeight > 100);

    if (this.options.size === 'cover') {
      this.element
        .querySelectorAll('iframe, [data-thumbnail]')
        .forEach((element) => this.applyClip(element))
      ;
    }
  }

  /**
   * @protected
   */
  calculateSize() {
    // Can't calculate video size if video metadata is not loaded
    if (!this.metadata) {
      return;
    }

    // If `ratio` is set in options, it'll take precedence over `clip` option.
    if (this.options.ratio) {
      const { width, height } = this.metadata;
      const originRatio = this.metadata.ratio;

      if (this.options.ratio > originRatio) {
        const customHeight = width / this.options.ratio;

        this.options.clip = `${(height - customHeight) / 2 / height} 0`;
      } else if (this.options.ratio < originRatio) {
        const customWidth = height * this.options.ratio;

        this.options.clip = `0 ${(width - customWidth) / 2 / width}`;
      } else {
        this.options.clip = 0;
      }
    }

    // Calculate video size based on configured `clip`
    const position = { horizontal: 1, vertical: 1, top: 0, left: 0 };

    if (Number.isFinite(this.options.clip) || this.options.clip) {
      let edges = this.options.clip;
      let top, right, bottom, left;

      if (Number.isFinite(edges)) {
        edges = [edges];
      } else if (typeof edges === 'string') {
        edges = edges.replace(/\s+/, ' ').split(' ');
      }

      if (Array.isArray(edges)) {
        edges = edges.map(parseFloat);

        if (edges.length === 1) {
          top = right = bottom = left = edges[0];
        } else if (edges.length === 2) {
          ([top, right] = edges);

          bottom = top;
          left = right;
        } else if (edges.length === 3) {
          ([top, right, bottom] = edges);

          left = right;
        } else if (edges.length === 4) {
          [top, right, bottom, left] = edges;
        } else {
          console.warn(`Invalid video "clip" option with value "${this.options.clip}".`);
        }
      }

      if (edges.filter(Number.isFinite).length === edges.length) {
        Object.assign(position, { top, right, bottom, left });

        position.horizontal = 1 - left - right;
        position.vertical = 1 - top - bottom;
      }
    }

    position.percentageHeight = Math.round(this.metadata.height / this.metadata.width * 100000 * position.vertical / position.horizontal) / 1000;
    this.setPosition(position);
  }
}
